import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Select, Switch, Typography, Spin, Input } from 'antd';
import apiClient from '../../../api/client';

const { Text } = Typography;

/**
 * TopoLineEditorPanel — Procedural topographic contour line generator for the Scrapbook.
 *
 * Generates organic, map-inspired contour lines using simplex noise, marching squares,
 * and Chaikin curve smoothing. Supports 5 terrain modes and full color/styling controls.
 *
 * Two modes:
 *  - Effect mode: composites contours onto an existing layer (sourceImageUrl provided)
 *  - Standalone mode: generates contour pattern as new layer (sourceImageUrl is null)
 *
 * Props:
 *  - visible, onClose
 *  - onApply(dataUrl) — effect mode: replace layer image
 *  - onAddAsLayer(dataUrl) — standalone mode: add as new scrapbook layer
 *  - sourceImageUrl, proxyUrl — for effect mode overlay
 *  - canvasId — for palette fetching
 */

// ─── Terrain Mode Definitions ───
const TERRAIN_MODES = [
    { value: 'perlin', label: 'Perlin Noise', icon: '🏔' },
    { value: 'ridge', label: 'Ridge', icon: '⛰' },
    { value: 'plateau', label: 'Plateau', icon: '🏜' },
    { value: 'island', label: 'Island', icon: '🏝' },
    { value: 'erosion', label: 'Erosion', icon: '🌊' },
];

const BLEND_MODES = [
    { value: 'source-over', label: 'Normal' },
    { value: 'multiply', label: 'Multiply' },
    { value: 'screen', label: 'Screen' },
    { value: 'overlay', label: 'Overlay' },
    { value: 'soft-light', label: 'Soft Light' },
];

// ═══════════════════════════════════════════════════════
// ─── Simplex Noise (2D) — self-contained implementation
// ═══════════════════════════════════════════════════════

const buildNoise = (seed) => {
    // Seeded PRNG (Mulberry32)
    const mulberry32 = (s) => {
        let a = s | 0;
        return () => {
            a |= 0; a = a + 0x6D2B79F5 | 0;
            let t = Math.imul(a ^ a >>> 15, 1 | a);
            t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
            return ((t ^ t >>> 14) >>> 0) / 4294967296;
        };
    };

    const rng = mulberry32(seed);

    // Permutation table
    const perm = new Uint8Array(512);
    const p = new Uint8Array(256);
    for (let i = 0; i < 256; i++) p[i] = i;
    for (let i = 255; i > 0; i--) {
        const j = (rng() * (i + 1)) | 0;
        [p[i], p[j]] = [p[j], p[i]];
    }
    for (let i = 0; i < 512; i++) perm[i] = p[i & 255];

    // Gradient vectors for 2D simplex
    const grad2 = [
        [1, 1], [-1, 1], [1, -1], [-1, -1],
        [1, 0], [-1, 0], [0, 1], [0, -1],
    ];

    const F2 = 0.5 * (Math.sqrt(3) - 1);
    const G2 = (3 - Math.sqrt(3)) / 6;

    const noise2D = (x, y) => {
        const s = (x + y) * F2;
        const i = Math.floor(x + s);
        const j = Math.floor(y + s);
        const t = (i + j) * G2;
        const X0 = i - t;
        const Y0 = j - t;
        const x0 = x - X0;
        const y0 = y - Y0;

        let i1, j1;
        if (x0 > y0) { i1 = 1; j1 = 0; }
        else { i1 = 0; j1 = 1; }

        const x1 = x0 - i1 + G2;
        const y1 = y0 - j1 + G2;
        const x2 = x0 - 1 + 2 * G2;
        const y2 = y0 - 1 + 2 * G2;

        const ii = i & 255;
        const jj = j & 255;

        const dot = (g, dx, dy) => g[0] * dx + g[1] * dy;

        let n0 = 0, n1 = 0, n2 = 0;

        let t0 = 0.5 - x0 * x0 - y0 * y0;
        if (t0 > 0) {
            t0 *= t0;
            const gi0 = perm[ii + perm[jj]] % 8;
            n0 = t0 * t0 * dot(grad2[gi0], x0, y0);
        }

        let t1 = 0.5 - x1 * x1 - y1 * y1;
        if (t1 > 0) {
            t1 *= t1;
            const gi1 = perm[ii + i1 + perm[jj + j1]] % 8;
            n1 = t1 * t1 * dot(grad2[gi1], x1, y1);
        }

        let t2 = 0.5 - x2 * x2 - y2 * y2;
        if (t2 > 0) {
            t2 *= t2;
            const gi2 = perm[ii + 1 + perm[jj + 1]] % 8;
            n2 = t2 * t2 * dot(grad2[gi2], x2, y2);
        }

        // Scale to [-1, 1]
        return 70 * (n0 + n1 + n2);
    };

    return noise2D;
};

// ─── Fractal noise (octaved) ───
const fractalNoise = (noise2D, x, y, octaves, lacunarity, persistence) => {
    let value = 0;
    let amplitude = 1;
    let frequency = 1;
    let maxValue = 0;
    for (let i = 0; i < octaves; i++) {
        value += noise2D(x * frequency, y * frequency) * amplitude;
        maxValue += amplitude;
        amplitude *= persistence;
        frequency *= lacunarity;
    }
    return value / maxValue;
};

// ─── Chaikin's corner-cutting smoothing ───
const chaikinSmooth = (points, iterations = 2) => {
    let pts = points;
    for (let iter = 0; iter < iterations; iter++) {
        const next = [];
        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[i];
            const p1 = pts[i + 1];
            next.push([
                0.75 * p0[0] + 0.25 * p1[0],
                0.75 * p0[1] + 0.25 * p1[1],
            ]);
            next.push([
                0.25 * p0[0] + 0.75 * p1[0],
                0.25 * p0[1] + 0.75 * p1[1],
            ]);
        }
        pts = next;
    }
    return pts;
};

// ─── Marching Squares — extract isolines from a scalar field ───
const marchingSquares = (field, w, h, threshold) => {
    const segments = [];

    const val = (x, y) => field[y * w + x];

    const lerp = (a, b, va, vb) => {
        if (Math.abs(va - vb) < 1e-10) return 0.5;
        return (threshold - va) / (vb - va);
    };

    for (let y = 0; y < h - 1; y++) {
        for (let x = 0; x < w - 1; x++) {
            const tl = val(x, y);
            const tr = val(x + 1, y);
            const br = val(x + 1, y + 1);
            const bl = val(x, y + 1);

            let code = 0;
            if (tl >= threshold) code |= 8;
            if (tr >= threshold) code |= 4;
            if (br >= threshold) code |= 2;
            if (bl >= threshold) code |= 1;

            if (code === 0 || code === 15) continue;

            // Interpolated edge midpoints
            const top = [x + lerp(x, x + 1, tl, tr), y];
            const right = [x + 1, y + lerp(y, y + 1, tr, br)];
            const bottom = [x + lerp(x, x + 1, bl, br), y + 1];
            const left = [x, y + lerp(y, y + 1, tl, bl)];

            // Lookup — which edges to connect
            switch (code) {
                case 1: segments.push([left, bottom]); break;
                case 2: segments.push([bottom, right]); break;
                case 3: segments.push([left, right]); break;
                case 4: segments.push([top, right]); break;
                case 5: segments.push([top, right]); segments.push([left, bottom]); break; // saddle
                case 6: segments.push([top, bottom]); break;
                case 7: segments.push([left, top]); break;
                case 8: segments.push([left, top]); break;
                case 9: segments.push([top, bottom]); break;
                case 10: segments.push([top, left]); segments.push([bottom, right]); break; // saddle
                case 11: segments.push([top, right]); break;
                case 12: segments.push([left, right]); break;
                case 13: segments.push([bottom, right]); break;
                case 14: segments.push([left, bottom]); break;
                default: break;
            }
        }
    }

    return segments;
};

// ─── Chain line segments into polylines ───
const chainSegments = (segments) => {
    if (segments.length === 0) return [];

    // Build adjacency from segment endpoints
    const EPS = 0.01;
    const key = (p) => `${Math.round(p[0] * 100)},${Math.round(p[1] * 100)}`;

    const adj = new Map();
    const addEdge = (a, b, idx) => {
        const ka = key(a);
        if (!adj.has(ka)) adj.set(ka, []);
        adj.get(ka).push({ point: b, idx });
    };

    const used = new Uint8Array(segments.length);
    segments.forEach((seg, i) => {
        addEdge(seg[0], seg[1], i);
        addEdge(seg[1], seg[0], i);
    });

    const chains = [];

    for (let i = 0; i < segments.length; i++) {
        if (used[i]) continue;
        used[i] = 1;

        const chain = [segments[i][0], segments[i][1]];

        // Extend forward
        let extending = true;
        while (extending) {
            extending = false;
            const lastKey = key(chain[chain.length - 1]);
            const neighbors = adj.get(lastKey);
            if (neighbors) {
                for (const n of neighbors) {
                    if (!used[n.idx]) {
                        used[n.idx] = 1;
                        chain.push(n.point);
                        extending = true;
                        break;
                    }
                }
            }
        }

        // Extend backward
        extending = true;
        while (extending) {
            extending = false;
            const firstKey = key(chain[0]);
            const neighbors = adj.get(firstKey);
            if (neighbors) {
                for (const n of neighbors) {
                    if (!used[n.idx]) {
                        used[n.idx] = 1;
                        chain.unshift(n.point);
                        extending = true;
                        break;
                    }
                }
            }
        }

        if (chain.length >= 2) {
            chains.push(chain);
        }
    }

    return chains;
};


// ═══════════════════════════════════════════════════════
// ─── Main Component
// ═══════════════════════════════════════════════════════

const TopoLineEditorPanel = ({ visible, onClose, onApply, onAddAsLayer, sourceImageUrl, proxyUrl, canvasId }) => {
    const previewCanvasRef = useRef(null);
    const sourceImgRef = useRef(null);
    const processingRef = useRef(false);
    const pendingRef = useRef(false);

    // Terrain mode
    const [terrainMode, setTerrainMode] = useState('perlin');

    // Contour controls
    const [contourCount, setContourCount] = useState(20);
    const [noiseScale, setNoiseScale] = useState(1.5);
    const [smoothness, setSmoothness] = useState(3);  // Chaikin iterations
    const [lineWeight, setLineWeight] = useState(1.5);
    const [jitter, setJitter] = useState(0);

    // Noise controls
    const [octaves, setOctaves] = useState(4);
    const [lacunarity, setLacunarity] = useState(2.0);
    const [persistence, setPersistence] = useState(0.5);

    // Offset (pan the noise field)
    const [offsetX, setOffsetX] = useState(0);
    const [offsetY, setOffsetY] = useState(0);

    // Major / minor lines
    const [majorEvery, setMajorEvery] = useState(5);
    const [majorWeight, setMajorWeight] = useState(3);

    // Ridge-specific
    const [sharpness, setSharpness] = useState(1.5);

    // Plateau-specific
    const [stepCount, setStepCount] = useState(8);

    // Island-specific
    const [falloffStrength, setFalloffStrength] = useState(2.0);

    // Erosion-specific
    const [flowDetail, setFlowDetail] = useState(3);

    // Fill between contours
    const [fillContours, setFillContours] = useState(false);
    const [fillOpacity, setFillOpacity] = useState(0.15);

    // Colors
    const [fgColor, setFgColor] = useState('#c084fc');
    const [bgColor, setBgColor] = useState('#0d1117');
    const [transparentBg, setTransparentBg] = useState(false);
    const [useGradient, setUseGradient] = useState(false);
    const [gradientEnd, setGradientEnd] = useState('#22d3ee');
    const [gradientAngle, setGradientAngle] = useState(0);

    // Blend (effect mode)
    const [blendMode, setBlendMode] = useState('source-over');
    const [blendOpacity, setBlendOpacity] = useState(0.85);

    // Seed
    const [seed, setSeed] = useState(42);

    // Palettes
    const [palettes, setPalettes] = useState([]);

    const [loading, setLoading] = useState(false);
    const isEffectMode = !!sourceImageUrl;

    // Load source image for effect mode
    useEffect(() => {
        if (!visible) return;
        if (sourceImageUrl) {
            setLoading(true);
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => { sourceImgRef.current = img; setLoading(false); };
            img.onerror = () => { sourceImgRef.current = null; setLoading(false); };
            img.src = proxyUrl || sourceImageUrl;
        } else {
            sourceImgRef.current = null;
        }
    }, [visible, sourceImageUrl, proxyUrl]);

    // Fetch palettes
    useEffect(() => {
        if (!visible || !canvasId) return;
        const fetchPalettes = async () => {
            try {
                const res = await apiClient.get(`/artboards/${canvasId}/palettes`);
                if (res.data?.palettes) {
                    setPalettes(res.data.palettes);
                }
            } catch (err) {
                // Silently ignore — palettes are optional
            }
        };
        fetchPalettes();
    }, [visible, canvasId]);

    // Re-render on any param change
    useEffect(() => {
        if (!visible) return;
        if (isEffectMode && !sourceImgRef.current && loading) return;
        renderTopo();
    }, [visible, terrainMode, contourCount, noiseScale, smoothness, lineWeight, jitter,
        octaves, lacunarity, persistence, offsetX, offsetY, majorEvery, majorWeight,
        sharpness, stepCount, falloffStrength, flowDetail,
        fillContours, fillOpacity,
        fgColor, bgColor, transparentBg, useGradient, gradientEnd, gradientAngle,
        blendMode, blendOpacity, seed, loading]);

    const renderTopo = useCallback(() => {
        if (!previewCanvasRef.current) return;
        if (processingRef.current) { pendingRef.current = true; return; }
        processingRef.current = true;
        requestAnimationFrame(() => {
            try {
                doRender(previewCanvasRef.current, 800);
            } finally {
                processingRef.current = false;
                if (pendingRef.current) { pendingRef.current = false; renderTopo(); }
            }
        });
    }, [terrainMode, contourCount, noiseScale, smoothness, lineWeight, jitter,
        octaves, lacunarity, persistence, offsetX, offsetY, majorEvery, majorWeight,
        sharpness, stepCount, falloffStrength, flowDetail,
        fillContours, fillOpacity,
        fgColor, bgColor, transparentBg, useGradient, gradientEnd, gradientAngle,
        blendMode, blendOpacity, seed]);

    // ═══════════════════════════════════════════════════════
    // ─── RENDER ENGINE
    // ═══════════════════════════════════════════════════════

    const doRender = (canvas, maxDim) => {
        let w, h;
        const img = sourceImgRef.current;

        if (img) {
            const s = Math.min(1, maxDim / Math.max(img.width, img.height));
            w = Math.round(img.width * s);
            h = Math.round(img.height * s);
        } else {
            w = maxDim;
            h = maxDim;
        }

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');

        // Draw background / source image
        if (img) {
            ctx.drawImage(img, 0, 0, w, h);
        } else if (!transparentBg) {
            ctx.fillStyle = bgColor;
            ctx.fillRect(0, 0, w, h);
        }

        // ─── Generate the height field ───
        const fieldRes = 200; // Resolution of noise sampling (lower = faster)
        const noise2D = buildNoise(seed);
        const field = new Float32Array(fieldRes * fieldRes);

        const scaleX = noiseScale * 3;
        const scaleY = noiseScale * 3;
        const ox = offsetX * 0.1;
        const oy = offsetY * 0.1;

        for (let y = 0; y < fieldRes; y++) {
            for (let x = 0; x < fieldRes; x++) {
                const nx = (x / fieldRes) * scaleX + ox;
                const ny = (y / fieldRes) * scaleY + oy;

                let val = fractalNoise(noise2D, nx, ny, octaves, lacunarity, persistence);

                // Apply terrain mode transforms
                switch (terrainMode) {
                    case 'ridge':
                        val = 1 - Math.abs(val);
                        val = Math.pow(val, sharpness);
                        break;

                    case 'plateau': {
                        // Quantize to steps
                        val = (val + 1) / 2; // normalize to 0..1
                        val = Math.floor(val * stepCount) / stepCount;
                        val = val * 2 - 1; // back to -1..1
                        break;
                    }

                    case 'island': {
                        // Radial falloff from center
                        const cx = x / fieldRes - 0.5;
                        const cy = y / fieldRes - 0.5;
                        const dist = Math.sqrt(cx * cx + cy * cy) * 2; // 0 at center, ~1 at edges
                        const falloff = Math.pow(Math.max(0, 1 - dist), falloffStrength);
                        val = val * falloff;
                        break;
                    }

                    case 'erosion': {
                        // Layer multiple noise octaves with different characteristics
                        const erosionNoise = fractalNoise(noise2D, nx * 2, ny * 2, flowDetail, 2.5, 0.4);
                        // Create gully-like features by using absolute value and subtracting
                        const gullies = Math.abs(erosionNoise) * 0.5;
                        val = val - gullies;
                        break;
                    }

                    // 'perlin' — use raw fractal noise, no transform
                    default:
                        break;
                }

                // Add jitter
                if (jitter > 0) {
                    const jNoise = noise2D(nx * 10, ny * 10);
                    val += jNoise * jitter * 0.15;
                }

                field[y * fieldRes + x] = val;
            }
        }

        // ─── Extract contour lines via marching squares ───
        const topoCanvas = document.createElement('canvas');
        topoCanvas.width = w;
        topoCanvas.height = h;
        const tCtx = topoCanvas.getContext('2d');

        // Compute min/max of height field for adaptive contouring
        let minVal = Infinity, maxVal = -Infinity;
        for (let i = 0; i < field.length; i++) {
            if (field[i] < minVal) minVal = field[i];
            if (field[i] > maxVal) maxVal = field[i];
        }

        const range = maxVal - minVal;
        if (range < 0.001) {
            // Flat field, nothing to draw
            ctx.save();
            if (img) {
                ctx.globalCompositeOperation = blendMode;
                ctx.globalAlpha = blendOpacity;
            }
            ctx.drawImage(topoCanvas, 0, 0);
            ctx.restore();
            return;
        }

        // Build stroke color/gradient
        const getStroke = () => {
            if (useGradient) {
                const angleRad = (gradientAngle * Math.PI) / 180;
                const halfW = w / 2;
                const halfH = h / 2;
                const len = Math.sqrt(halfW * halfW + halfH * halfH);
                const x1 = halfW - Math.cos(angleRad) * len;
                const y1 = halfH - Math.sin(angleRad) * len;
                const x2 = halfW + Math.cos(angleRad) * len;
                const y2 = halfH + Math.sin(angleRad) * len;
                const grad = tCtx.createLinearGradient(x1, y1, x2, y2);
                grad.addColorStop(0, fgColor);
                grad.addColorStop(1, gradientEnd);
                return grad;
            }
            return fgColor;
        };

        const strokeStyle = getStroke();

        // Helper: parse hex to RGBA components for elevation tinting
        const hexToRgba = (hex) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(1, 3), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return { r, g, b };
        };

        // Scale factor from field coords to canvas coords
        const sx = w / fieldRes;
        const sy = h / fieldRes;

        // Draw each contour level
        for (let c = 0; c < contourCount; c++) {
            const t = minVal + (c + 1) * (range / (contourCount + 1));
            const isMajor = (c + 1) % majorEvery === 0;

            // Extract segments
            const segments = marchingSquares(field, fieldRes, fieldRes, t);
            if (segments.length === 0) continue;

            // Chain into polylines
            const chains = chainSegments(segments);

            // Fill between contours (elevation bands)
            if (fillContours && c < contourCount - 1) {
                const elevation = (c + 1) / (contourCount + 1);
                tCtx.save();
                tCtx.globalAlpha = fillOpacity;
                // Tint based on elevation: lerp between bg-ish and fg-ish
                const r = Math.round(13 + elevation * 100);
                const g = Math.round(17 + elevation * 60);
                const b = Math.round(35 + elevation * 150);
                tCtx.fillStyle = useGradient
                    ? `rgba(${r}, ${g}, ${b}, ${fillOpacity})`
                    : fgColor;

                chains.forEach(chain => {
                    if (chain.length < 3) return;
                    let pts = chain.map(p => [p[0] * sx, p[1] * sy]);
                    if (smoothness > 0) {
                        pts = chaikinSmooth(pts, Math.min(smoothness, 4));
                    }
                    tCtx.beginPath();
                    tCtx.moveTo(pts[0][0], pts[0][1]);
                    for (let i = 1; i < pts.length; i++) {
                        tCtx.lineTo(pts[i][0], pts[i][1]);
                    }
                    tCtx.closePath();
                    tCtx.fill();
                });
                tCtx.restore();
            }

            // Draw contour lines
            tCtx.save();
            tCtx.strokeStyle = strokeStyle;
            tCtx.lineWidth = isMajor ? majorWeight : lineWeight;
            tCtx.lineCap = 'round';
            tCtx.lineJoin = 'round';
            tCtx.globalAlpha = isMajor ? 1.0 : 0.7;

            chains.forEach(chain => {
                // Scale from field coordinates to canvas coordinates
                let pts = chain.map(p => [p[0] * sx, p[1] * sy]);

                // Smooth
                if (smoothness > 0 && pts.length >= 3) {
                    pts = chaikinSmooth(pts, Math.min(smoothness, 4));
                }

                if (pts.length < 2) return;

                tCtx.beginPath();
                tCtx.moveTo(pts[0][0], pts[0][1]);
                for (let i = 1; i < pts.length; i++) {
                    tCtx.lineTo(pts[i][0], pts[i][1]);
                }
                tCtx.stroke();
            });

            tCtx.restore();
        }

        // ─── Composite onto main canvas ───
        ctx.save();
        if (img) {
            ctx.globalCompositeOperation = blendMode;
            ctx.globalAlpha = blendOpacity;
        }
        ctx.drawImage(topoCanvas, 0, 0);
        ctx.restore();

        // Preserve original alpha if in effect mode
        if (img) {
            const alphaCanvas = document.createElement('canvas');
            alphaCanvas.width = w;
            alphaCanvas.height = h;
            const alphaCtx = alphaCanvas.getContext('2d');
            alphaCtx.drawImage(img, 0, 0, w, h);
            ctx.globalCompositeOperation = 'destination-in';
            ctx.drawImage(alphaCanvas, 0, 0);
            ctx.globalCompositeOperation = 'source-over';
        }
    };


    // ═══════════════════════════════════════════════════════
    // ─── ACTIONS
    // ═══════════════════════════════════════════════════════

    const handleApply = useCallback(() => {
        const fullCanvas = document.createElement('canvas');
        const img = sourceImgRef.current;
        if (img) {
            doRender(fullCanvas, Math.max(img.width, img.height));
        } else {
            doRender(fullCanvas, 1024);
        }
        const dataUrl = fullCanvas.toDataURL('image/png');
        if (isEffectMode) {
            onApply?.(dataUrl);
        } else {
            onAddAsLayer?.(dataUrl);
        }
    }, [terrainMode, contourCount, noiseScale, smoothness, lineWeight, jitter,
        octaves, lacunarity, persistence, offsetX, offsetY, majorEvery, majorWeight,
        sharpness, stepCount, falloffStrength, flowDetail,
        fillContours, fillOpacity,
        fgColor, bgColor, transparentBg, useGradient, gradientEnd, gradientAngle,
        blendMode, blendOpacity, seed,
        onApply, onAddAsLayer, isEffectMode]);


    // ═══════════════════════════════════════════════════════
    // ─── UI HELPERS
    // ═══════════════════════════════════════════════════════

    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{
                    track: { background: '#22d3ee' },
                    rail: { background: '#30363d' },
                    handle: { borderColor: '#22d3ee', width: 12, height: 12 }
                }}
            />
            <Text style={{ color: '#22d3ee', fontSize: 11, fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(2) : value}
            </Text>
        </div>
    );

    const toggleRow = (label, value, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80 }}>{label}</Text>
            <Switch checked={value} onChange={onChange} size="small" style={{ background: value ? '#22d3ee' : '#30363d' }} />
        </div>
    );

    const colorRow = (label, value, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80 }}>{label}</Text>
            <div style={{ position: 'relative' }}>
                <div style={{
                    width: 24, height: 24, borderRadius: 4,
                    background: value, border: '1px solid #30363d', cursor: 'pointer',
                }} onClick={() => document.getElementById(`topo-color-${label}`).click()} />
                <input
                    id={`topo-color-${label}`}
                    type="color"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={{ position: 'absolute', top: 0, left: 0, width: 24, height: 24, opacity: 0, cursor: 'pointer' }}
                />
            </div>
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                size="small"
                style={{
                    width: 80,
                    background: '#21262d',
                    borderColor: '#30363d',
                    color: '#c9d1d9',
                    fontSize: 11,
                    fontFamily: 'monospace',
                }}
            />
        </div>
    );

    if (!visible) return null;

    // ═══════════════════════════════════════════════════════
    // ─── RENDER
    // ═══════════════════════════════════════════════════════

    return (
        <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 110, display: 'flex',
            background: 'rgba(13, 17, 23, 0.95)',
            pointerEvents: 'auto',
        }}
            onClick={(e) => e.stopPropagation()}>

            {/* Preview Area */}
            <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24, overflow: 'hidden',
            }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin size="large" />
                        <Text style={{ color: '#8b949e' }}>Loading image...</Text>
                    </div>
                ) : (
                    <canvas ref={previewCanvasRef}
                        style={{
                            maxWidth: '100%', maxHeight: '100%',
                            borderRadius: 8,
                            boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
                            border: '1px solid #30363d',
                        }} />
                )}
            </div>

            {/* Controls Sidebar */}
            <div style={{
                width: 320, flexShrink: 0,
                borderLeft: '1px solid #30363d',
                display: 'flex', flexDirection: 'column',
                background: '#0d1117',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', borderBottom: '1px solid #30363d',
                    background: '#161b22', flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>⛰</span>
                        <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 14 }}>
                            Topographic {isEffectMode ? '(Effect)' : '(Generate)'}
                        </span>
                    </div>
                    <Button type="text" size="small" onClick={onClose}
                        style={{ color: '#8b949e', fontSize: 12, padding: '2px 6px' }}>✕</Button>
                </div>

                {/* Scrollable Controls */}
                <div style={{
                    flex: 1, overflowY: 'auto', padding: '12px 14px',
                    display: 'flex', flexDirection: 'column', gap: 16,
                }}>

                    {/* Terrain Mode Selector */}
                    <div>
                        <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Terrain Mode
                        </Text>
                        <div style={{
                            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 4, marginTop: 8,
                        }}>
                            {TERRAIN_MODES.map((mode) => (
                                <button
                                    key={mode.value}
                                    onClick={() => setTerrainMode(mode.value)}
                                    style={{
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', gap: 2,
                                        padding: '6px 4px', borderRadius: 6,
                                        background: terrainMode === mode.value ? 'rgba(34, 211, 238, 0.15)' : '#21262d',
                                        border: terrainMode === mode.value ? '1px solid #22d3ee' : '1px solid #30363d',
                                        color: terrainMode === mode.value ? '#22d3ee' : '#8b949e',
                                        cursor: 'pointer', fontSize: 10, fontWeight: 500,
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <span style={{ fontSize: 16 }}>{mode.icon}</span>
                                    {mode.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Contour Controls */}
                    <div>
                        <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Contours
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Lines', 5, 50, contourCount, setContourCount)}
                            {sliderRow('Scale', 0.2, 5.0, noiseScale, (v) => setNoiseScale(v), 0.1)}
                            {sliderRow('Smoothness', 0, 5, smoothness, setSmoothness)}
                            {sliderRow('Line Weight', 0.5, 6, lineWeight, (v) => setLineWeight(v), 0.5)}
                            {sliderRow('Jitter', 0, 1, jitter, (v) => setJitter(v), 0.01)}
                        </div>
                    </div>

                    {/* Major / Minor Lines */}
                    <div>
                        <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Major / Minor
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Major Every', 2, 10, majorEvery, setMajorEvery)}
                            {sliderRow('Major Weight', 1, 8, majorWeight, (v) => setMajorWeight(v), 0.5)}
                        </div>
                    </div>

                    {/* Noise Controls */}
                    <div>
                        <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Noise Field
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Octaves', 1, 8, octaves, setOctaves)}
                            {sliderRow('Lacunarity', 1.0, 4.0, lacunarity, (v) => setLacunarity(v), 0.1)}
                            {sliderRow('Persistence', 0.1, 1.0, persistence, (v) => setPersistence(v), 0.05)}
                            {sliderRow('Offset X', -20, 20, offsetX, (v) => setOffsetX(v), 0.5)}
                            {sliderRow('Offset Y', -20, 20, offsetY, (v) => setOffsetY(v), 0.5)}
                        </div>
                    </div>

                    {/* Mode-Specific Controls */}
                    {terrainMode === 'ridge' && (
                        <div>
                            <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Ridge
                            </Text>
                            <div style={{ marginTop: 8 }}>
                                {sliderRow('Sharpness', 0.5, 4.0, sharpness, (v) => setSharpness(v), 0.1)}
                            </div>
                        </div>
                    )}

                    {terrainMode === 'plateau' && (
                        <div>
                            <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Plateau
                            </Text>
                            <div style={{ marginTop: 8 }}>
                                {sliderRow('Step Count', 3, 20, stepCount, setStepCount)}
                            </div>
                        </div>
                    )}

                    {terrainMode === 'island' && (
                        <div>
                            <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Island
                            </Text>
                            <div style={{ marginTop: 8 }}>
                                {sliderRow('Falloff', 0.5, 5.0, falloffStrength, (v) => setFalloffStrength(v), 0.1)}
                            </div>
                        </div>
                    )}

                    {terrainMode === 'erosion' && (
                        <div>
                            <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Erosion
                            </Text>
                            <div style={{ marginTop: 8 }}>
                                {sliderRow('Flow Detail', 1, 8, flowDetail, setFlowDetail)}
                            </div>
                        </div>
                    )}

                    {/* Fill Between Contours */}
                    <div>
                        <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Elevation Fill
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {toggleRow('Fill Bands', fillContours, setFillContours)}
                            {fillContours && sliderRow('Fill Opacity', 0.05, 0.5, fillOpacity, (v) => setFillOpacity(v), 0.01)}
                        </div>
                    </div>

                    {/* Colors */}
                    <div>
                        <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Color
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {colorRow('Foreground', fgColor, setFgColor)}
                            {!isEffectMode && colorRow('Background', bgColor, setBgColor)}
                            {!isEffectMode && toggleRow('Transparent', transparentBg, setTransparentBg)}
                            {toggleRow('Gradient', useGradient, setUseGradient)}
                            {useGradient && colorRow('Gradient End', gradientEnd, setGradientEnd)}
                            {useGradient && sliderRow('Angle', 0, 360, gradientAngle, setGradientAngle)}
                        </div>

                        {/* Palette presets */}
                        {palettes.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text style={{ color: '#6e7681', fontSize: 10 }}>Palettes</Text>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                                    {palettes.slice(0, 6).map((palette, pi) => (
                                        <button
                                            key={pi}
                                            onClick={() => {
                                                const colors = palette.colors || [];
                                                if (colors.length >= 1) setFgColor(colors[0]);
                                                if (colors.length >= 2) setBgColor(colors[1]);
                                                if (colors.length >= 3) { setUseGradient(true); setGradientEnd(colors[2]); }
                                            }}
                                            style={{
                                                display: 'flex', gap: 1, padding: 2,
                                                background: '#21262d', border: '1px solid #30363d',
                                                borderRadius: 4, cursor: 'pointer',
                                            }}
                                        >
                                            {(palette.colors || []).slice(0, 4).map((c, ci) => (
                                                <div key={ci} style={{
                                                    width: 12, height: 12, background: c,
                                                    borderRadius: ci === 0 ? '2px 0 0 2px' : ci === (palette.colors || []).slice(0, 4).length - 1 ? '0 2px 2px 0' : 0,
                                                }} />
                                            ))}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Blend (effect mode only) */}
                    {isEffectMode && (
                        <div>
                            <Text style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Blend
                            </Text>
                            <Select value={blendMode} onChange={setBlendMode}
                                style={{ width: '100%', marginTop: 6 }}
                                options={BLEND_MODES}
                            />
                            <div style={{ marginTop: 8 }}>
                                {sliderRow('Opacity', 0, 1, blendOpacity, (v) => setBlendOpacity(v), 0.01)}
                            </div>
                        </div>
                    )}

                    {/* Re-roll */}
                    <Button size="small" type="text"
                        onClick={() => setSeed(Date.now())}
                        style={{ color: '#6e7681', fontSize: 11, alignSelf: 'flex-start' }}>
                        🎲 Re-roll Seed
                    </Button>
                </div>

                {/* Action Footer */}
                <div style={{
                    padding: '12px 14px', borderTop: '1px solid #30363d',
                    display: 'flex', gap: 8, flexShrink: 0, background: '#161b22',
                }}>
                    <Button onClick={onClose}
                        style={{ flex: 1, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}>
                        Cancel
                    </Button>
                    <Button type="primary" onClick={handleApply}
                        style={{ flex: 1, background: '#22d3ee', borderColor: '#22d3ee', color: '#000' }}>
                        {isEffectMode ? 'Apply' : 'Add as Layer'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TopoLineEditorPanel;
